import React, { useState, useEffect } from 'react';

import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import {
  handleApiError as handleApiErrorRaw,
  handleNetworkError as handleNetworkErrorRaw,
} from '@utils/apiErrorHandler';

// Loose-cast .jsx util signatures so JSDoc-derived "all required" param shapes don't reject our partial options
const handleApiError = handleApiErrorRaw as (response: unknown, options?: Record<string, unknown>) => void;
const handleNetworkError = handleNetworkErrorRaw as (error: unknown, options?: Record<string, unknown>) => void;
import { Button, Form, Input, message, Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';

const { TextArea } = Input;

interface MerchantOption {
  id: string;
  name: string;
}

interface BrandFormResponse {
  success: boolean;
  data?: { id: string; [key: string]: unknown };
  [key: string]: unknown;
}

interface BrandEditService {
  // 服務來源混雜（admin: .js any、operator: .ts Zod-inferred 具體 shape）— return 一律 Promise<any>
  get: (id: string) => Promise<any>;
  create: (data: any) => Promise<any>;
  update: (id: string, data: any) => Promise<any>;
  [key: string]: unknown;
}

interface BrandEditProps {
  brandService: BrandEditService;
  role?: 'admin' | 'operator';
  merchants?: MerchantOption[];
  // admin caller 仍會傳 showMerchantSelect={false}，雖然此元件內部不讀；保留 prop 以避免 TS 報錯
  showMerchantSelect?: boolean;
}

const BrandEdit: React.FC<BrandEditProps> = ({
  brandService,
  role = 'admin',
  merchants = [],
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const isNew = !id;
  const goBack = useSmartBack();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const operatorData = useOperatorData() as { merchant?: { id?: string } } | undefined;
  const isOperator = role === 'operator';
  const showMerchantSelect = isOperator && isNew && !operatorData?.merchant?.id;
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!isNew);

  // 將 operator context 的 merchant_id 同步到 form（僅 Operator）
  // 抽成 callback 以便 resetFields 後重新套用
  const applyOperatorMerchantId = () => {
    if (isOperator && operatorData?.merchant?.id) {
      form.setFieldsValue({ merchant_id: operatorData.merchant.id });
    }
  };

  useEffect(() => {
    applyOperatorMerchantId();
  }, [isOperator, operatorData?.merchant?.id]);

  useEffect(() => {
    if (!isNew) {
      loadBrand();
    }
  }, [id]);

  const loadBrand = async () => {
    if (!id) return;
    setInitialLoading(true);
    try {
      const response = await brandService.get(id);
      if (response.success) {
        form.setFieldsValue(response.data);
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.load_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Load brand error:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  const submitValues = async (values: Record<string, unknown>): Promise<BrandFormResponse> => {
    const response = isNew
      ? await brandService.create(values)
      : await brandService.update(id as string, values);
    return response;
  };

  // 主要提交：成功後導向 show 頁（新建與編輯皆適用）
  const handleSubmit = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      const response = await submitValues(values);

      if (response.success) {
        message.success(t(isNew ? 'shared.brands.form.success' : 'common.message.update_success') as string);
        // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到新資料
        // React Router basename 會自動補上 /operator 或 /admin 前綴
        const recordId = response.data?.id || id;
        navigate(`/brands/${recordId}`, { replace: true });
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: t(isNew ? 'shared.brands.form.error' : 'common.error.update_failed')
        });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Submit brand error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);

        if (response.success) {
          message.success(t('shared.brands.form.success') as string);
          // 重置表單，然後重新套用 operator 的 merchant_id（避免被 resetFields 清掉）
          form.resetFields();
          applyOperatorMerchantId();
        } else {
          handleApiError(response, {
            t,
            fallbackMessage: t('shared.brands.form.error')
          });
        }
      } catch (error) {
        handleNetworkError(error, { t });
        console.error('Submit brand error:', error);
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  const basePath = role === 'operator' ? '/operator' : '/admin';

  // 僅新建模式顯示「儲存並新增下一筆」
  const extraButtons = isNew ? (
    <Button
      loading={savingAndAdding}
      disabled={loading}
      onClick={handleSaveAndAddAnother}
    >
      {t('common.button.save_and_add_another')}
    </Button>
  ) : null;

  return (
    <div>
      <PageHeader
        basePath={basePath}
        items={['products', 'products.brands', { label: isNew ? t('common.button.create') : t('common.button.edit') }]}
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        {showMerchantSelect ? (
          <Form.Item
            label={t('models.brand.merchant')}
            name="merchant_id"
            rules={[
              { required: true, message: t('common.form.required') as string }
            ]}
          >
            <Select
              placeholder={t('common.form.select') as string}
              options={merchants.map((merchant) => ({
                label: merchant.name,
                value: merchant.id,
              }))}
            />
          </Form.Item>
        ) : (
          <Form.Item name="merchant_id" hidden><Input /></Form.Item>
        )}

        <Form.Item
          label={t('models.brand.name')}
          name="name"
          rules={[
            { required: true, message: t('common.form.required') as string }
          ]}
        >
          <Input placeholder={t('common.placeholder.please_enter') as string} />
        </Form.Item>

        <Form.Item
          label={t('models.brand.description')}
          name="description"
        >
          <TextArea
            rows={4}
            placeholder={t('common.placeholder.please_enter') as string}
          />
        </Form.Item>

        <FloatingActionBarSpacer />
      </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={savingAndAdding}
        extraButtons={extraButtons}
        submitText={t(isNew ? 'common.button.create' : 'common.button.save') as string}
      />
    </div>
  );
};

export default BrandEdit;
